/**
 * EP-2 Per-Brand Configurable Helper Budgets — TanStack Query hooks.
 *
 * Backs the EP-2 sysadmin UI surfaces:
 *   /sysadmin/platform/helper-budgets         — matrix editor (§9.1)
 *   /sysadmin/models/$brand                   — brand drill-down (§9.2)
 *   /admin/helper-budgets                     — tenant-admin read-only (§9.3)
 *
 * Schemas live HERE so the wire types stay in lockstep with the
 * Fastify route schemas in
 *   packages/api/src/routes/v1/admin/ep2/helper-budgets.ts
 */

import { z } from 'zod';
import {
  createSysadminQuery,
  createSysadminMutation,
} from './use-sysadmin-query';

// ─── Shared schemas ──────────────────────────────────────────────────────

export const SourceEnum = z.enum(['default', 'operator', 'emergency_override']);
export type Source = z.infer<typeof SourceEnum>;

export const BudgetValuesSchema = z.object({
  firstTokenBudgetMs: z.number().int().min(1000).max(600_000),
  streamInactivityBudgetMs: z.number().int().min(1000).max(600_000),
  totalBudgetMs: z.number().int().min(5000).max(1_800_000),
  heartbeatIntervalMs: z.number().int().min(1000).max(60_000),
});
export type BudgetValues = z.infer<typeof BudgetValuesSchema>;

export const ResolutionSourceSchema = z.enum([
  'db_explicit',
  'db_inherited_brand',
  'db_inherited_kind',
  'db_factory',
]);
export type ResolutionSource = z.infer<typeof ResolutionSourceSchema>;

export const HelperBudgetRowSchema = z.object({
  id: z.string().uuid(),
  brandId: z.string().uuid().nullable(),
  helperKind: z.string().nullable(),
  firstTokenBudgetMs: z.number().int(),
  streamInactivityBudgetMs: z.number().int(),
  totalBudgetMs: z.number().int(),
  heartbeatIntervalMs: z.number().int(),
  source: SourceEnum,
  notes: z.string().nullable(),
  createdBy: z.string().uuid().nullable(),
  updatedBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type HelperBudgetRow = z.infer<typeof HelperBudgetRowSchema>;

export const MatrixCellSchema = z.object({
  brandId: z.string().uuid(),
  brandSlug: z.string(),
  helperKind: z.string(),
  resolved: BudgetValuesSchema,
  source: ResolutionSourceSchema,
  sourceRowId: z.string().uuid(),
  sloBaseline: z
    .object({
      firstTokenP50Ms: z.number().int(),
      firstTokenP95Ms: z.number().int(),
      contextSizeTokens: z.number().int(),
      sampledAt: z.string(),
    })
    .nullable(),
});
export type MatrixCell = z.infer<typeof MatrixCellSchema>;

export const HelperBudgetMatrixResponseSchema = z.object({
  brands: z.array(
    z.object({
      id: z.string().uuid(),
      slug: z.string(),
      displayName: z.string(),
      modality: z.string().nullable(),
    }),
  ),
  helperKinds: z.array(z.string()),
  rows: z.array(HelperBudgetRowSchema),
  matrix: z.array(MatrixCellSchema),
});
export type HelperBudgetMatrixResponse = z.infer<
  typeof HelperBudgetMatrixResponseSchema
>;

// ─── Sysadmin matrix query ────────────────────────────────────────────────

export const useHelperBudgetMatrix = createSysadminQuery({
  key: ['sysadmin-ep2-helper-budgets'],
  url: '/admin/platform/helper-budgets',
  schema: HelperBudgetMatrixResponseSchema,
});

// ─── Create row ───────────────────────────────────────────────────────────

export const CreateHelperBudgetSchema = z
  .object({
    brandId: z.string().uuid().nullable(),
    helperKind: z.string().nullable(),
    notes: z.string().max(2000).nullable().optional(),
  })
  .merge(BudgetValuesSchema);
export type CreateHelperBudget = z.infer<typeof CreateHelperBudgetSchema>;

export const useCreateHelperBudget = createSysadminMutation<
  CreateHelperBudget,
  { id: string }
>({
  url: '/admin/platform/helper-budgets',
  method: 'POST',
  inputSchema: CreateHelperBudgetSchema,
  outputSchema: z.object({ id: z.string().uuid() }),
  invalidates: [['sysadmin-ep2-helper-budgets']],
});

// ─── Update row ───────────────────────────────────────────────────────────

export const UpdateHelperBudgetSchema = z
  .object({
    notes: z.string().max(2000).nullable().optional(),
  })
  .merge(BudgetValuesSchema.partial());
export type UpdateHelperBudget = z.infer<typeof UpdateHelperBudgetSchema>;

/**
 * Dynamic-URL mutation. Caller invokes `useUpdateHelperBudget(rowId)`
 * to bind the row id at hook-construction time.
 */
export const useUpdateHelperBudget = (rowId: string) =>
  createSysadminMutation<UpdateHelperBudget, { id: string }>({
    url: `/admin/platform/helper-budgets/${rowId}`,
    method: 'PUT',
    inputSchema: UpdateHelperBudgetSchema,
    outputSchema: z.object({ id: z.string().uuid() }),
    invalidates: [['sysadmin-ep2-helper-budgets']],
  })();

// ─── Delete row ───────────────────────────────────────────────────────────

export const useDeleteHelperBudget = (rowId: string) =>
  createSysadminMutation<undefined, undefined>({
    url: `/admin/platform/helper-budgets/${rowId}`,
    method: 'DELETE',
    inputSchema: z.undefined(),
    outputSchema: z.undefined(),
    invalidates: [['sysadmin-ep2-helper-budgets']],
  })();

// ─── Seed from EP-1 baselines ─────────────────────────────────────────────

export const SeedFromBaselinesInputSchema = z.object({
  dryRun: z.boolean().default(true),
  overwriteOperatorRows: z.boolean().default(false),
});
export type SeedFromBaselinesInput = z.infer<typeof SeedFromBaselinesInputSchema>;

export const SeedFromBaselinesResponseSchema = z.object({
  dryRun: z.boolean(),
  inserts: z.array(
    z.object({
      brandId: z.string().uuid(),
      brandSlug: z.string(),
      firstTokenBudgetMs: z.number().int(),
      streamInactivityBudgetMs: z.number().int(),
      totalBudgetMs: z.number().int(),
      heartbeatIntervalMs: z.number().int(),
    }),
  ),
  skippedOperatorRows: z.array(
    z.object({
      brandId: z.string().uuid(),
      brandSlug: z.string(),
      reason: z.string(),
    }),
  ),
  brandsWithoutBaseline: z.array(
    z.object({ brandId: z.string().uuid(), brandSlug: z.string() }),
  ),
});
export type SeedFromBaselinesResponse = z.infer<
  typeof SeedFromBaselinesResponseSchema
>;

export const useSeedFromSloBaselines = createSysadminMutation<
  SeedFromBaselinesInput,
  SeedFromBaselinesResponse
>({
  url: '/admin/platform/helper-budgets/seed-from-slo-baselines',
  method: 'POST',
  inputSchema: SeedFromBaselinesInputSchema,
  outputSchema: SeedFromBaselinesResponseSchema,
  invalidates: [['sysadmin-ep2-helper-budgets']],
});

// ─── Emergency override ───────────────────────────────────────────────────

export const EmergencyOverrideSchema = z.object({
  rowId: z.string().uuid(),
  totalBudgetMs: z.number().int().min(5000).max(1_800_000),
  ttlSeconds: z.number().int().min(60).max(86_400),
  notes: z.string().min(20),
});
export type EmergencyOverride = z.infer<typeof EmergencyOverrideSchema>;

export const useEmergencyOverride = createSysadminMutation<
  EmergencyOverride,
  { id: string; expiresAt: string }
>({
  url: '/admin/platform/helper-budgets/emergency-override',
  method: 'POST',
  inputSchema: EmergencyOverrideSchema,
  outputSchema: z.object({
    id: z.string().uuid(),
    expiresAt: z.string(),
  }),
  invalidates: [['sysadmin-ep2-helper-budgets']],
});

// ─── Tenant-admin read-only matrix (§9.3) ─────────────────────────────────

export const TenantHelperBudgetMatrixResponseSchema = z.object({
  brands: z.array(
    z.object({
      id: z.string().uuid(),
      slug: z.string(),
      displayName: z.string(),
    }),
  ),
  helperKinds: z.array(z.string()),
  matrix: z.array(
    z.object({
      brandId: z.string().uuid(),
      brandSlug: z.string(),
      helperKind: z.string(),
      resolved: BudgetValuesSchema,
      source: ResolutionSourceSchema,
    }),
  ),
});
export type TenantHelperBudgetMatrixResponse = z.infer<
  typeof TenantHelperBudgetMatrixResponseSchema
>;

/**
 * Tenant-admin read-only view. Uses the regular admin auth (not
 * sysadmin) — the createSysadminQuery helper is reused here because
 * both paths share the same JSON parsing + error handling; the
 * underlying fetch wrapper picks the right auth based on the URL.
 *
 * NOTE: Despite the `createSysadminQuery` name, this hook's URL points
 * to `/admin/helper-budgets` (tenant-admin scope). If the wire helper
 * gets refactored to require sysadmin specifically, replace this with
 * an admin-scope hook.
 */
export const useTenantHelperBudgets = createSysadminQuery({
  key: ['admin-ep2-helper-budgets'],
  url: '/admin/helper-budgets',
  schema: TenantHelperBudgetMatrixResponseSchema,
});
